/**
 * O casco do sistema interno: barra lateral escura, topo branco e a área de
 * conteúdo rolável.
 *
 * Existe uma vez e vale para as vinte telas que faltam. É por isso que ele
 * não conhece nenhuma delas: recebe título, subtítulo e o conteúdo, e o resto
 * — navegação, identidade, saída — sai da sessão.
 *
 * # Quem rola, e quem não rola
 *
 * A altura é `h-screen` e o container não rola: quem rola é SÓ o conteúdo. A
 * versão anterior usava `min-h-screen`, e o efeito era que a barra lateral
 * crescia junto com a página — o bloco do usuário, que mora no pé dela, só
 * aparecia depois de rolar o dashboard inteiro. Navegação que exige rolar para
 * ser alcançada deixou de ser navegação.
 *
 * # Onde mora o quê
 *
 * O TOPO é contexto de quem está operando: saudação, pendências e a empresa em
 * que a sessão está. O TÍTULO DA TELA e as ações dela (cadastrar, exportar)
 * moram no conteúdo, logo acima do que titulam — colar título de tela na mesma
 * barra que a identidade da conta mistura duas escalas de navegação, e a ação
 * primária acaba a dois metros do que ela cria.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { Icone } from '../../componentes/Icone'
import { Logo } from '../../componentes/Marca'
import { useSessao } from '../../plataforma/contexto-sessao'
import { buscarEmpresa, type Empresa } from '../../plataforma/empresa'
import { mascararCNPJ } from '../../plataforma/mascaras'
import { carregarPainel, type Aviso } from '../../plataforma/painel'
import { NAVEGACAO, type ItemNav } from './navegacao'

/**
 * Quanto da área útil a tela ocupa.
 *
 * `total` é o padrão e serve a tudo que tem tabela, grade ou rail: essas telas
 * ganham com cada pixel de largura.
 *
 * `formulario` existe porque o contrário também é verdade. Um formulário numa
 * área de 1.688 px tinha duas saídas ruins, e o sistema usava as duas: ou o
 * campo virava uma linha de 1.600 px que ninguém varre com o olho, ou o
 * conteúdo era travado por dentro (`max-w-[820px]`) e o cartão ficava com
 * metade branca à direita — que é o que se via em "Dados da empresa" e em "Meu
 * perfil".
 *
 * A saída certa é limitar a COLUNA e centralizá-la: o cartão tem a largura do
 * que carrega, e a sobra fica repartida dos dois lados em vez de empilhada num
 * canto só.
 */
export type LarguraDoConteudo = 'total' | 'formulario' | 'foco'

/*
  As três larguras, e o critério para escolher entre elas:

  `total`       tem tabela, grade ou rail. Ganha com cada pixel.
  `formulario`  formulário DENSO, que se organiza em duas colunas. 1.180 px é o
                que cabe em duas colunas com o gap de 16 px sem que nenhuma
                passe de 580 px — acima disso um campo de texto deixa de
                parecer um campo e começa a parecer uma faixa.
  `foco`        formulário CURTO, de coluna única: perfil, troca de senha. Aqui
                1.180 px seria pior que 1.688: um campo "Nome" ocupando a
                largura inteira não fica confortável, fica exagerado — o olho
                perde a relação entre o rótulo à esquerda e o fim do campo, e a
                tela parece vazia mesmo cheia.

  A regra curta: a largura acompanha o CONTEÚDO do campo, não o espaço
  disponível.
*/
const COLUNA: Record<LarguraDoConteudo, string> = {
  total: 'w-full',
  formulario: 'mx-auto w-full max-w-[1180px]',
  foco: 'mx-auto w-full max-w-[760px]',
}

/**
 * Um degrau da trilha. Sem `para`, é o degrau atual — que não vira link.
 *
 * O último degrau NUNCA é link: um link que aponta para a página em que já se
 * está é ruído, e o leitor de tela anuncia `aria-current="page"` no lugar.
 */
export type Degrau = { rotulo: string; para?: string }

export function LayoutApp({
  titulo,
  subtitulo,
  trilha,
  voltarPara,
  acoes,
  largura = 'total',
  children,
}: {
  titulo: string
  subtitulo: string
  /**
   * O caminho até aqui — "Clientes › Novo cliente".
   *
   * Antes isto viajava DENTRO do subtítulo, como texto ("Clientes / Novo
   * cliente"), e por ser texto não dava para clicar em "Clientes" para voltar.
   * O subtítulo, quando há trilha, volta a ser o que ele deveria ser: o que a
   * tela está mostrando, e não onde ela fica.
   */
  trilha?: Degrau[]
  /**
   * Mostra a seta de voltar à esquerda do título.
   *
   * `'auto'` volta no HISTÓRICO, que é quase sempre o certo: quem abriu a
   * edição a partir da ficha espera voltar para a ficha, não para o topo da
   * listagem. Um caminho fixo só quando a tela pode ser aberta por link direto
   * e o histórico não teria para onde voltar.
   */
  voltarPara?: string | 'auto'
  acoes?: ReactNode
  largura?: LarguraDoConteudo
  children: ReactNode
}) {
  const { situacao, sair, podeVer } = useSessao()
  const sessao = situacao.estado === 'autenticado' ? situacao.sessao : null
  const local = useLocation()

  // A guia diz em que tela a pessoa está, como no Garage ("Clientes - Garage").
  // Numa SPA o título é definido uma vez no index.html e nunca mais muda, e o
  // resultado é que quem trabalha com seis guias abertas não distingue nenhuma.
  useEffect(() => {
    document.title = `${titulo} - Lumma Store`
  }, [titulo])

  const grupos = NAVEGACAO.map((grupo) => ({
    ...grupo,
    itens: grupo.itens.filter((item) => podeVer(item.permissao)),
  })).filter((grupo) => grupo.itens.length > 0)

  return (
    // `h-dvh` e não `h-screen`: `100vh` é a viewport IDEAL, que em vários
    // navegadores é maior que a área realmente visível. A diferença vira
    // rolagem no documento — e aí a barra lateral sobe junto com o conteúdo e
    // aparece uma faixa branca embaixo do casco. `100dvh` é a área visível de
    // fato, que é o que "a tela inteira e nada além" quer dizer aqui.
    //
    // A `nav` herda a altura do pai (`h-full`) em vez de repetir a da
    // viewport: duas alturas de tela irmãs é como uma delas fica para trás no
    // dia em que a outra mudar.
    <div className="flex h-dvh overflow-hidden bg-fundo">
      <nav
        aria-label="Navegação principal"
        className="flex h-full w-[232px] shrink-0 flex-col bg-tinta px-3 py-5"
      >
        {/* Mesmo arranjo do Garage: glifo, nome do produto e a casa embaixo.
            Os dois sistemas dividem a barra lateral escura e o "by Lumma
            Software" é o que diz ao lojista que são a mesma família. */}
        <div className="flex shrink-0 items-center gap-[10px] px-[10px] pb-6">
          <Logo tamanho={30} />
          <div className="flex flex-col leading-none">
            <span className="font-titulo text-[17px] font-bold text-white">Lumma Store</span>
            <span className="mt-[3px] text-[11.5px] text-texto-suave">by Lumma Software</span>
          </div>
        </div>

        {/* Só a LISTA rola. O bloco do usuário abaixo fica onde está, mesmo
            quando o menu crescer além da tela. */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {grupos.map((grupo) => (
            <div key={grupo.titulo} className="mb-[18px]">
              <div className="px-3 pb-1.5 text-[10.5px] font-bold tracking-[0.6px] text-texto-suave uppercase">
                {grupo.titulo}
              </div>
              {grupo.itens.map((item) => (
                <ItemDaBarra
                  key={item.destino}
                  item={item}
                  atual={ehORotaAtual(local.pathname, item.destino)}
                />
              ))}
            </div>
          ))}
        </div>

        {/* O bloco de quem está operando fecha a barra — é o lugar onde a
            pessoa procura o próprio nome, e onde "Sair" é esperado. */}
        <div className="shrink-0 border-t border-menu-realce pt-3">
          <MenuDoUsuario
            iniciais={sessao?.usuario.iniciais ?? ''}
            nome={sessao?.usuario.nome ?? ''}
            email={sessao?.usuario.email ?? ''}
            papel={sessao?.usuario.papel_rotulo ?? ''}
            aoSair={() => void sair()}
          />
        </div>
      </nav>

      <main className="flex min-w-0 flex-1 flex-col">
        {/* ── o topo é de quem opera, não da tela ── */}
        <header className="flex h-[64px] shrink-0 items-center gap-3 border-b border-borda bg-superficie px-7">
          <p className="min-w-0 truncate text-[13.5px] text-texto">
            {saudacao()},{' '}
            <span className="font-semibold text-tinta">{primeiroNome(sessao?.usuario.nome ?? '')}</span>
          </p>

          <div className="flex-1" />

          <SinoDePendencias />
          {/* Nome fantasia primeiro: é o nome que a pessoa reconhece como
              "sua loja". A razão social entra quando não há fantasia. */}
          <BlocoDaEmpresa
            nome={sessao?.loja.nome_fantasia || sessao?.loja.nome || ''}
            cnpj={sessao?.loja.cnpj ?? ''}
          />
        </header>

        {/*
          `px-7` é o MESMO recuo do cabeçalho acima. Não é escolha estética: é
          o que faz a borda esquerda dos cartões cair na mesma vertical do
          título. Com 20px aqui e 28px lá, tudo ficava 8px deslocado — e a tela
          parecia empurrada para a esquerda.
        */}
        {/*
          `relative` não é enfeite — é o que impede o documento inteiro de
          ganhar rolagem, e a razão é sutil o bastante para merecer estar
          escrita.

          `overflow: auto` cria um contexto de ROLAGEM, mas NÃO cria um bloco
          de contenção para `position: absolute`. Só criam isso `position`
          diferente de `static`, `transform`, `filter`, `contain` e afins.
          Sem `relative` aqui, todo elemento absoluto dentro do conteúdo
          escapava deste container e ancorava no `<body>` — na posição em que
          estaria no fluxo, que numa tabela longa é a 1200px do topo.

          E há dezenas deles em toda tela, escondidos num lugar improvável: a
          classe `sr-only` do Tailwind é `position: absolute`. Cada
          `<caption class="sr-only">` de tabela e cada `<span class="sr-only">`
          de rótulo acessível virava um ponto de ancoragem lá embaixo,
          esticando o DOCUMENTO — nunca o `body`, que continuava com a altura
          da janela.

          O efeito para quem usa: a página rolava por fora, a barra lateral
          escura subia junto e sobrava uma faixa branca embaixo do casco. Foi
          assim que o defeito apareceu — e ele não estava na tela nenhuma, mas
          numa classe de acessibilidade que ninguém suspeitaria.
        */}
        <div className="relative flex-1 overflow-auto px-7 py-6">
          {/* O título acompanha a coluna: cabeçalho na largura toda com o
              conteúdo estreito embaixo deixaria as ações longe do que elas
              operam, que é o problema oposto ao que a coluna resolve. */}
          <div className={COLUNA[largura]}>
            {trilha && trilha.length > 0 && <Trilha degraus={trilha} />}

            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2.5">
                {voltarPara && <BotaoVoltar para={voltarPara} />}
                <div className="min-w-0">
                  <h1 className="font-titulo text-[26px] leading-tight font-bold text-titulo">
                    {titulo}
                  </h1>
                  <p className="mt-1 text-[13.5px] text-texto-suave">{subtitulo}</p>
                </div>
              </div>
              {acoes && <div className="flex shrink-0 flex-wrap items-center gap-2.5">{acoes}</div>}
            </div>

            {children}
          </div>
        </div>
      </main>
    </div>
  )
}

/**
 * A trilha — spec 01, "Shell": breadcrumb acima do título.
 *
 * É um `<nav>` com lista ordenada porque é isso que ela é: uma sequência com
 * ordem que importa. O leitor de tela anuncia "navegação estrutural, lista de
 * 2 itens", e não três textos soltos separados por barra.
 *
 * O separador vai em `aria-hidden`: "›" lido em voz alta a cada degrau é ruído
 * puro, e a estrutura da lista já comunica a hierarquia.
 */
function Trilha({ degraus }: { degraus: Degrau[] }) {
  return (
    <nav aria-label="Trilha de navegação" className="mb-2">
      <ol className="flex flex-wrap items-center gap-1.5 p-0 text-[12.5px]">
        {degraus.map((degrau, i) => {
          const ultimo = i === degraus.length - 1
          return (
            <li key={degrau.rotulo} className="flex items-center gap-1.5">
              {i > 0 && (
                <span aria-hidden className="text-inativo">
                  ›
                </span>
              )}
              {degrau.para && !ultimo ? (
                <Link
                  to={degrau.para}
                  className="rounded-sistema font-medium text-texto-suave hover:text-marca-forte hover:underline"
                >
                  {degrau.rotulo}
                </Link>
              ) : (
                <span aria-current={ultimo ? 'page' : undefined} className="font-semibold text-texto">
                  {degrau.rotulo}
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

/**
 * A seta de voltar, colada no título.
 *
 * `'auto'` volta no histórico e é o padrão certo: quem chegou à edição pela
 * ficha aberta na gaveta espera voltar para a ficha. Um destino fixo mandaria
 * essa pessoa para o topo da listagem, perdendo o lugar onde ela estava.
 *
 * Quando o destino é fixo, é um `<Link>` de verdade — abre em nova aba, entra
 * na lista de links do leitor de tela e mostra o endereço na barra de status.
 * `'auto'` não tem como ser link: não existe href para "de onde eu vim".
 */
function BotaoVoltar({ para }: { para: string | 'auto' }) {
  const navegar = useNavigate()

  const classe =
    'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-sistema border border-borda bg-superficie text-texto transition-colors duration-150 hover:border-inativo hover:bg-divisoria'

  if (para === 'auto') {
    return (
      <button type="button" onClick={() => navegar(-1)} className={classe}>
        <Icone nome="arrow_back" tamanho={19} />
        <span className="sr-only">Voltar</span>
      </button>
    )
  }

  return (
    <Link to={para} className={classe}>
      <Icone nome="arrow_back" tamanho={19} />
      <span className="sr-only">Voltar</span>
    </Link>
  )
}

/** "Bom dia" até 12h, "Boa tarde" até 18h, "Boa noite" depois. */
function saudacao(agora = new Date()): string {
  const hora = agora.getHours()
  if (hora < 12) return 'Bom dia'
  if (hora < 18) return 'Boa tarde'
  return 'Boa noite'
}

function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? ''
}

/**
 * Diz se o item do menu é o lugar em que a pessoa está.
 *
 * Compara por PREFIXO, e não por igualdade: as telas de um módulo moram sob a
 * rota dele (`/app/compras/notas-fiscais/importar`), e o item do menu apagava
 * assim que alguém entrava numa delas — dava a impressão de ter saído do
 * módulo. `/app` é a exceção: é prefixo de todas as rotas, então só casa
 * exatamente.
 */
function ehORotaAtual(caminho: string, destino: string): boolean {
  if (destino === '/app') return caminho === '/app'
  return caminho === destino || caminho.startsWith(destino + '/')
}

function ItemDaBarra({ item, atual }: { item: ItemNav; atual: boolean }) {
  const conteudo = (
    <>
      <Icone nome={item.icone} tamanho={20} />
      <span className="flex-1">{item.rotulo}</span>
      {item.abrirEmNovaAba && (
        // Indica a saída para outra aba ANTES do clique — a mesma promessa que
        // um `target="_blank"` silencioso quebra para quem usa leitor de tela.
        <Icone nome="abre_em_nova_aba" tamanho={14} className="text-texto-suave" />
      )}
      {!item.pronta && (
        <span className="rounded-full bg-menu-realce px-[7px] py-[2px] text-[10px] font-bold text-texto-fraco">
          em breve
        </span>
      )}
    </>
  )

  const base = 'flex w-full items-center gap-[11px] rounded-sistema px-3 py-2.5 text-[13.5px]'

  if (!item.pronta) {
    return (
      // `aria-disabled` e não `disabled`: o item continua alcançável pela
      // leitura de tela, que é como a pessoa descobre que a função existe e
      // ainda não está pronta.
      <span aria-disabled className={`${base} cursor-default text-texto-suave`}>
        {conteudo}
      </span>
    )
  }

  return (
    <Link
      to={item.destino}
      target={item.abrirEmNovaAba ? '_blank' : undefined}
      rel={item.abrirEmNovaAba ? 'noopener noreferrer' : undefined}
      aria-current={atual ? 'page' : undefined}
      className={`${base} font-semibold ${
        atual ? 'bg-marca text-white' : 'text-texto-fraco hover:bg-menu-realce hover:text-white'
      }`}
    >
      {conteudo}
    </Link>
  )
}

/**
 * O sino do topo — e ele mostra PENDÊNCIA DE VERDADE.
 *
 * Não existe sistema de notificação neste produto, e um sino que abre vazio
 * para sempre é enfeite que ensina a ignorar sinos. O que existe são os avisos
 * do painel — ruptura de grade, estoque abaixo do mínimo, tarefa do primeiro
 * acesso —, e é exatamente isso que alguém espera encontrar aqui.
 *
 * Carrega SÓ ao abrir: o layout envolve todas as telas, e uma chamada a mais
 * em cada uma delas custaria em toda navegação para um número que quase sempre
 * ninguém olha.
 */
function SinoDePendencias() {
  const [aberto, setAberto] = useState(false)
  const [avisos, setAvisos] = useState<Aviso[] | null>(null)
  const [erro, setErro] = useState(false)

  useEffect(() => {
    if (!aberto || avisos) return
    const controle = new AbortController()
    carregarPainel('7d', controle.signal)
      .then((painel) => setAvisos(painel.avisos))
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(true)
      })
    return () => controle.abort()
  }, [aberto, avisos])

  useEffect(() => {
    if (!aberto) return
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAberto(false)
    }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  }, [aberto])

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setAberto((estava) => !estava)}
        aria-expanded={aberto}
        aria-haspopup="menu"
        className={`flex h-9 w-9 items-center justify-center rounded-sistema ${
          aberto ? 'bg-divisoria text-tinta' : 'text-texto-suave hover:bg-divisoria'
        }`}
      >
        <Icone nome="sino" tamanho={19} />
        <span className="sr-only">Pendências</span>
      </button>

      {aberto && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setAberto(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div
            role="menu"
            className="absolute top-11 right-0 z-50 w-[320px] rounded-sistema border border-borda bg-superficie py-1.5 shadow-[0_12px_28px_rgba(15,23,42,0.14)]"
          >
            <p className="border-b border-divisoria px-3.5 pt-1 pb-2 text-[11px] font-bold tracking-[0.6px] text-texto-suave uppercase">
              Pendências
            </p>

            {erro ? (
              <p className="px-3.5 py-3 text-[13px] text-texto-suave">
                Não foi possível carregar as pendências agora.
              </p>
            ) : !avisos ? (
              <p className="px-3.5 py-3 text-[13px] text-texto-suave">Carregando…</p>
            ) : avisos.length === 0 ? (
              <p className="px-3.5 py-3 text-[13px] text-texto-suave">
                Nada pendente. O que precisa de decisão aparece aqui.
              </p>
            ) : (
              avisos.map((aviso) => (
                <div key={aviso.tipo + aviso.titulo} className="border-b border-divisoria px-3.5 py-2.5 last:border-b-0">
                  <p
                    className={`text-[13px] font-semibold ${
                      aviso.tom === 'atencao' ? 'text-atencao-texto' : 'text-tinta'
                    }`}
                  >
                    {aviso.titulo}
                  </p>
                  <p className="mt-0.5 text-[12.5px] leading-[1.5] text-texto-suave">{aviso.detalhe}</p>
                  {aviso.destino && aviso.acao && (
                    <Link
                      to={aviso.destino}
                      onClick={() => setAberto(false)}
                      className="mt-1 inline-block text-[12.5px] font-semibold text-marca hover:underline"
                    >
                      {aviso.acao}
                    </Link>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * A empresa no topo, clicável.
 *
 * Ela está no cabeçalho e não no menu da conta porque não é preferência de
 * usuário: é o CONTEXTO do que está na tela. Quem opera duas lojas precisa
 * ver, sem abrir nada, em qual delas acabou de lançar a venda — e o CNPJ
 * embaixo do nome é o que distingue duas filiais de nome parecido.
 *
 * É um `Link`, não um botão que abre menu: existe um destino só, e um menu de
 * um item é uma parada a mais no caminho.
 */
/**
 * O bloco da loja, no topo — e o menu DA CONTA que ele abre.
 *
 * Era um link direto para `/app/empresa`. Virou popover porque a conta tem
 * mais de uma coisa a dizer, e as outras não cabiam em lugar nenhum:
 *
 *   · o PLANO e os dias de avaliação, que estavam perdidos no fim de "Dados da
 *     empresa" — uma tela que se define como "identidade que sai nos
 *     documentos" e onde o próprio bloco avisava "não se editam por aqui";
 *   · as UNIDADES, pela mesma razão.
 *
 * Os dois são da EMPRESA. O menu do usuário, no pé da barra lateral, é da
 * PESSOA — e misturar os dois faria dois administradores da mesma loja verem
 * "seu" plano em menus separados.
 *
 * O conteúdo carrega SÓ quando o menu abre: `GET /empresa` em toda navegação
 * seria uma requisição por tela para mostrar dois números que quase nunca
 * mudam.
 */
function BlocoDaEmpresa({ nome, cnpj }: { nome: string; cnpj: string }) {
  const [aberto, setAberto] = useState(false)
  const [empresa, setEmpresa] = useState<Empresa | null>(null)

  useEffect(() => {
    if (!aberto || empresa) return
    const controle = new AbortController()
    buscarEmpresa(controle.signal)
      .then(setEmpresa)
      .catch(() => {
        // Silêncio de propósito: o menu é contexto, não tarefa. Um erro
        // vermelho aqui assustaria por causa de um número que a pessoa nem
        // foi buscar — e "Dados da empresa" continua alcançável.
      })
    return () => controle.abort()
  }, [aberto, empresa])

  useEffect(() => {
    if (!aberto) return
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAberto(false)
    }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  }, [aberto])

  const emAvaliacao = (empresa?.dias_restantes ?? 0) > 0

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setAberto((estava) => !estava)}
        aria-expanded={aberto}
        aria-haspopup="menu"
        className={`flex max-w-[260px] min-w-0 items-center gap-2.5 rounded-sistema border px-3 py-1.5 ${
          aberto
            ? 'border-marca-borda bg-marca-fundo'
            : 'border-borda hover:border-marca-borda hover:bg-marca-fundo'
        }`}
      >
        <Icone nome="store" tamanho={18} className="shrink-0 text-texto-suave" />
        <span className="min-w-0 text-left">
          <span className="block truncate text-[13px] font-semibold text-tinta">{nome}</span>
          {/* A sessão traz o CNPJ em DÍGITOS — é assim que ele viaja e é assim
              que ele deve ser guardado. A máscara é da BORDA de apresentação,
              e é aqui: sem ela o topo mostrava "08268865000145", quatorze
              dígitos que ninguém lê como CNPJ. */}
          {cnpj && (
            <span className="tabular block truncate text-[11.5px] text-texto-suave">
              {mascararCNPJ(cnpj)}
            </span>
          )}
        </span>
        <Icone nome="chevron_direita" tamanho={16} className="shrink-0 rotate-90 text-texto-fraco" />
        <span className="sr-only">Abrir menu da loja {nome}</span>
      </button>

      {aberto && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setAberto(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div
            role="menu"
            className="absolute top-full right-0 z-50 mt-2 w-[300px] rounded-sistema border border-borda bg-superficie py-1.5 shadow-[0_12px_28px_rgba(15,23,42,0.14)]"
          >
            <div className="border-b border-divisoria px-3.5 pt-1 pb-2.5">
              <div className="truncate text-[13.5px] font-semibold text-titulo">{nome}</div>
              {cnpj && (
                <div className="tabular truncate text-xs text-texto-suave">{mascararCNPJ(cnpj)}</div>
              )}
            </div>

            {!empresa ? (
              <div className="px-3.5 py-3">
                <div className="h-3 w-28 animate-pulse rounded-full bg-divisoria" />
              </div>
            ) : (
              <>
                <div className="px-3.5 pt-2.5">
                  <div className="text-[10px] font-bold tracking-[0.6px] text-texto-fraco uppercase">
                    Plano
                  </div>
                  <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
                    <span className="text-[13px] font-semibold text-tinta">
                      {empresa.plano_rotulo}
                    </span>
                    <span className="text-[12px] text-texto-suave">{empresa.status_rotulo}</span>
                  </div>
                  {emAvaliacao && (
                    // Âmbar porque é prazo que corre: a spec 01 reserva a cor
                    // para o que espera decisão. Verde não pediria nada.
                    <div className="tabular mt-1.5 inline-flex items-center rounded-full bg-atencao-suave px-2 py-[2px] text-[11px] font-bold text-atencao-texto">
                      {empresa.dias_restantes}{' '}
                      {empresa.dias_restantes === 1 ? 'dia' : 'dias'} de avaliação
                    </div>
                  )}
                </div>

                <div className="mt-3 px-3.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[10px] font-bold tracking-[0.6px] text-texto-fraco uppercase">
                      Unidades
                    </span>
                    <span className="tabular text-[11.5px] font-semibold text-texto-suave">
                      {empresa.quantidade_filiais}
                    </span>
                  </div>
                  <ul className="mt-1.5 flex flex-col gap-1">
                    {empresa.filiais.map((filial) => (
                      <li key={filial.codigo} className="flex items-center gap-2">
                        <span className="tabular text-[11px] font-bold text-texto-fraco">
                          {filial.codigo}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[12.5px] text-texto">
                          {filial.nome}
                        </span>
                        {filial.principal && (
                          <span className="shrink-0 text-[10.5px] font-semibold text-texto-fraco">
                            principal
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {/* O menu MOSTRA; quem edita é a tela. */}
            <Link
              to="/app/empresa"
              role="menuitem"
              onClick={() => setAberto(false)}
              className="mt-2.5 flex w-full items-center gap-2.5 border-t border-divisoria px-3.5 pt-2.5 pb-1 text-[13.5px] font-semibold text-texto hover:text-marca"
            >
              <Icone nome="store" tamanho={18} />
              Dados da empresa
            </Link>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * O bloco de quem está operando, no pé da barra lateral.
 *
 * Fecha a navegação porque é ali que se procura o próprio nome e o "Sair" —
 * e porque o pé da barra é o único lugar da tela que não disputa espaço com
 * o conteúdo. O menu abre PARA CIMA pelo mesmo motivo: para baixo não há
 * tela.
 *
 * Não tem item para função que não existe (fatura, suporte, troca de
 * empresa): um menu maior que o sistema ensinaria que a função existe e
 * depois não levaria a lugar nenhum.
 */
function MenuDoUsuario({
  iniciais,
  nome,
  email,
  papel,
  aoSair,
}: {
  iniciais: string
  nome: string
  email: string
  papel: string
  aoSair: () => void
}) {
  const [aberto, setAberto] = useState(false)
  const caixa = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!aberto) return
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAberto(false)
    }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  }, [aberto])

  return (
    <div ref={caixa} className="relative">
      <button
        type="button"
        onClick={() => setAberto((estava) => !estava)}
        aria-expanded={aberto}
        aria-haspopup="menu"
        className={`flex w-full items-center gap-2.5 rounded-sistema px-[10px] py-2.5 text-left ${
          aberto ? 'bg-menu-realce' : 'hover:bg-menu-realce'
        }`}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-marca text-[11px] font-bold text-white">
          {iniciais}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold text-white">{nome}</span>
          {papel && <span className="block truncate text-[11.5px] text-texto-fraco">{papel}</span>}
        </span>
        <Icone nome="chevron_direita" tamanho={16} className="shrink-0 text-texto-fraco" />
        <span className="sr-only">Abrir menu da conta de {nome}</span>
      </button>

      {aberto && (
        <>
          {/* Camada de clique-fora. Um menu que só fecha no próprio botão fica
              aberto para sempre quando a pessoa clica em outro lugar. */}
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setAberto(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          {/* `bottom-full`: abre para cima, porque o bloco mora no pé da tela. */}
          <div
            role="menu"
            className="absolute bottom-full left-0 z-50 mb-2 max-h-[70vh] w-[280px] overflow-y-auto rounded-sistema border border-borda bg-superficie py-1.5 shadow-[0_12px_28px_rgba(15,23,42,0.14)]"
          >
            <div className="border-b border-divisoria px-3.5 pt-1 pb-2.5">
              <div className="truncate text-[13.5px] font-semibold text-titulo">{nome}</div>
              {email && <div className="truncate text-xs text-texto-suave">{email}</div>}
              {papel && (
                <span className="mt-1.5 inline-block rounded-full bg-marca-suave px-2 py-[2px] text-[10px] font-bold tracking-[0.3px] whitespace-nowrap text-marca-forte uppercase">
                  {papel}
                </span>
              )}
            </div>

            <Link
              to="/app/perfil"
              role="menuitem"
              onClick={() => setAberto(false)}
              className="mt-1 flex w-full items-center gap-2.5 px-3.5 py-2 text-[13.5px] font-semibold text-texto hover:bg-divisoria hover:text-marca"
            >
              <Icone nome="person" tamanho={18} />
              Meu perfil
            </Link>

            {/* Plano e unidades NÃO moram aqui: são da EMPRESA, e este menu é
                da PESSOA. Eles vivem no popover do bloco da loja, no topo. */}
            <button
              type="button"
              role="menuitem"
              onClick={aoSair}
              className="flex w-full items-center gap-2.5 px-3.5 py-2 text-[13.5px] font-semibold text-texto hover:bg-divisoria hover:text-erro"
            >
              <Icone nome="logout" tamanho={18} />
              Sair
            </button>
          </div>
        </>
      )}
    </div>
  )
}
